import { Graduations, Graduation } from './pages/Graduations';
import { AdminGraduations, AdminGraduation } from './pages/admin/Graduations';
import { Programs, Program } from './pages/Programs';
import { AdminPrograms } from './pages/admin/Programs';
import { AdminProgram } from './pages/admin/Program';
import { TrainingAutomations } from './pages/admin/TrainingAutomations';
import { SavedReports } from './pages/admin/SavedReports';
import { TrainingReports } from './pages/admin/TrainingReports';
import { QuestionBank } from './pages/admin/QuestionBank';
import { QuestionImport } from './pages/admin/QuestionImport';
import { InstructorCourses } from './pages/InstructorCourses';
import { EnrollmentRequests } from './pages/admin/EnrollmentRequests';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, Link, NavLink, useLocation } from 'react-router-dom';
import { api } from './api';
import { SessionProvider, useSession } from './session';
import { Logo } from './Brand';
import { Verify } from './pages/Verify';
import { MyCertificates } from './pages/MyCertificates';
import { MyClasses } from './pages/MyClasses';
import { SignIn } from './pages/SignIn';
import { ForgotPassword, ResetPassword } from './pages/ForgotPassword';
import { SetPassword } from './pages/SetPassword';
import { MyCourses } from './pages/MyCourses';
import { CoursePage } from './pages/CoursePage';
import { UnitPage } from './pages/UnitPage';
import { AdminHome } from './pages/admin/AdminHome';
import { People } from './pages/admin/People';
import { Person } from './pages/admin/Person';
import { NewPerson } from './pages/admin/NewPerson';
import { Courses } from './pages/admin/Courses';
import { AdminCourse } from './pages/admin/Course';
import { Cohorts } from './pages/admin/Cohorts';
import { Cohort } from './pages/admin/Cohort';
import { CourseEditor } from './pages/admin/CourseEditor';
import { UnitEditor } from './pages/admin/UnitEditor';
import { NewCourse } from './pages/admin/NewCourse';
import { Marking } from './pages/admin/Marking';
import { MarkTest } from './pages/admin/MarkTest';
import { MarkAssignment } from './pages/admin/MarkAssignment';
import { ImportPeople } from './pages/admin/ImportPeople';
import { Messages } from './pages/Messages';
import { Profile } from './pages/Profile';
import { Catalog } from './pages/Catalog';
import { QuestionStatsList, QuestionStatsDetail } from './pages/admin/QuestionStats';
import { CertificateDesigner } from './pages/admin/CertificateDesigner';
import { Files } from './pages/Files';
import { Progress } from './pages/Progress';
import { AdminFiles } from './pages/admin/Files';
import { AdminUserTypes } from './pages/admin/UserTypes';
import { AdminSignInReadiness } from './pages/admin/SignInReadiness';
import { Settings } from './pages/admin/Settings';
import { CourseDiscussions, DiscussionTopic } from './pages/Discussions';
import { SessionRegister } from './pages/admin/SessionRegister';
import { AdminCertificates } from './pages/admin/Certificates';
import { Reports } from './pages/admin/Reports';
import { Notifications } from './pages/admin/Notifications';

export function App() {
  return (
    <Routes>
      {/* Checking a certificate needs no account: it is the whole point of the
          reference printed on it. Kept outside the session shell so it never
          redirects a client or an auditor to a sign-in page. */}
      <Route path="/verify/:publicId" element={<Verify />} />
      <Route path="/verify/program/:publicId" element={<Verify program />} />
      <Route path="*" element={<SessionProvider><Shell /></SessionProvider>} />
    </Routes>
  );
}

/** True when the signed-in account holds a permission (prefix match, as on the server). */
function can(permissions: string[] | undefined, key: string) {
  return (permissions ?? []).some((p) => p === key || key.startsWith(p + '.'));
}

function Shell() {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading) return <div className="boot">Loading…</div>;
  if (!user) {
    // Recovering a password happens while signed out, so these two live here
    // rather than behind the sign-in wall they exist to get somebody past.
    if (location.pathname === '/forgot-password') return <ForgotPassword />;
    if (location.pathname === '/reset-password') return <ResetPassword />;
    return <SignIn />;
  }
  if (user.mustResetPassword && location.pathname !== '/password') return <SetPassword />;

  const isAdmin = can(user.permissions, 'administrator.users.read')
    || can(user.permissions, 'administrator.courses.read');
  // An instructor marks without being an administrator, so the marking screens
  // are gated separately from the admin ones.
  const canMark = can(user.permissions, 'instructor.courses.grade');
  const canTrainingReport = ['administrator.reports', 'instructor.reports', 'instructor.courses.view'].some(key => can(user.permissions, key));
  const canGroups = can(user.permissions, 'administrator.groups.read');
  const canAuthor = can(user.permissions, 'instructor.courses.view') || can(user.permissions, 'instructor.courses.create');
  const canApprove = can(user.permissions, 'administrator.courses.update') || can(user.permissions, 'instructor.courses.users.enroll');

  return (
    <>
      <ViewingAsBanner />
      <TopBar isAdmin={isAdmin} canMark={canMark} canAuthor={canAuthor} canApprove={canApprove} canGroups={canGroups} canTrainingReport={canTrainingReport} />
      <Announcement />
      <main className="page">
        <Routes>
          <Route path="/" element={<MyCourses />} />
          <Route path="/courses/:courseId" element={<CoursePage />} />
          <Route path="/courses/:courseId/units/:unitId" element={<UnitPage />} />
          <Route path="/password" element={<SetPassword />} />
          <Route path="/certificates" element={<MyCertificates />} />
          <Route path="/classes" element={<MyClasses />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/graduations" element={<Graduations />} />
          <Route path="/graduations/:id" element={<Graduation />} />
          {can(user.permissions, "administrator.reports") && <Route path="/admin/graduations" element={<AdminGraduations />} />}
          {can(user.permissions, "administrator.reports") && <Route path="/admin/graduations/:id" element={<AdminGraduation />} />}
          <Route path="/programs/:id" element={<Program />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/courses/:courseId/discussion" element={<CourseDiscussions />} />
          <Route path="/discussions/topics/:topicId" element={<DiscussionTopic />} />

          {canAuthor && <Route path="/instructor/courses" element={<InstructorCourses />} />}
          {canAuthor && <Route path="/instructor/courses/new" element={<NewCourse mode="instructor" />} />}
          {canAuthor && <Route path="/instructor/courses/:id/edit" element={<CourseEditor mode="instructor" />} />}
          {canAuthor && <Route path="/instructor/courses/:id/edit/units/:unitId/question-bank" element={<QuestionBank mode="instructor" />} />}
          {canAuthor && <Route path="/instructor/courses/:id/edit/units/:unitId/question-import" element={<QuestionImport mode="instructor" />} />}
          {canAuthor && <Route path="/instructor/courses/:id/edit/units/:unitId" element={<UnitEditor mode="instructor" />} />}
          {['read','update'].some(action => can(user.permissions, 'administrator.events_engine.notifications.' + action)) && <Route path="/admin/training-automations" element={<TrainingAutomations />} />}
          {canTrainingReport && <Route path="/admin/saved-reports" element={<SavedReports />} />}
          {canTrainingReport && <Route path="/admin/training-reports" element={<TrainingReports />} />}
          {canApprove && <Route path="/admin/enrollment-requests" element={<EnrollmentRequests />} />}
          {isAdmin && <Route path="/admin" element={<AdminHome />} />}
          {isAdmin && <Route path="/admin/people" element={<People />} />}
          {isAdmin && <Route path="/admin/people/new" element={<NewPerson />} />}
          {isAdmin && <Route path="/admin/people/import" element={<ImportPeople />} />}
          {isAdmin && <Route path="/admin/people/:id" element={<Person />} />}
          {isAdmin && <Route path="/admin/courses" element={<Courses />} />}
          {isAdmin && <Route path="/admin/courses/new" element={<NewCourse />} />}
          {isAdmin && <Route path="/admin/courses/:id/edit" element={<CourseEditor />} />}
          {isAdmin && <Route path="/admin/courses/:id/edit/units/:unitId/question-bank" element={<QuestionBank />} />}
          {isAdmin && <Route path="/admin/courses/:id/edit/units/:unitId/question-import" element={<QuestionImport />} />}
          {isAdmin && <Route path="/admin/courses/:id/edit/units/:unitId" element={<UnitEditor />} />}
          {isAdmin && <Route path="/admin/courses/:id" element={<AdminCourse />} />}
          {(canGroups || canTrainingReport) && <Route path="/admin/programs" element={<AdminPrograms />} />}
          {(canGroups || canTrainingReport) && <Route path="/admin/programs/:id" element={<AdminProgram />} />}
          {canGroups && <Route path="/admin/cohorts" element={<Cohorts />} />}
          {canGroups && <Route path="/admin/cohorts/:id" element={<Cohort />} />}
          <Route path="/files" element={<Files />} />
          <Route path="/progress" element={<Progress />} />
          <Route path="/progress/:userId" element={<Progress />} />
          {isAdmin && <Route path="/admin/files" element={<AdminFiles />} />}
          {isAdmin && <Route path="/admin/user-types" element={<AdminUserTypes />} />}
          {isAdmin && <Route path="/admin/passwords" element={<AdminSignInReadiness />} />}
          {isAdmin && <Route path="/admin/certificates" element={<AdminCertificates />} />}
          {isAdmin && <Route path="/admin/certificates/templates/:id" element={<CertificateDesigner />} />}
          {/* Instructors get reports about their own courses without being administrators. */}
          {canTrainingReport && <Route path="/admin/reports" element={<Reports />} />}
          {(isAdmin || canMark) && <Route path="/admin/question-stats" element={<QuestionStatsList />} />}
          {(isAdmin || canMark) && <Route path="/admin/question-stats/:unitId" element={<QuestionStatsDetail />} />}
          {isAdmin && <Route path="/admin/notifications" element={<Notifications />} />}
          {isAdmin && <Route path="/admin/settings" element={<Settings />} />}

          {canMark && <Route path="/marking" element={<Marking />} />}
          {canMark && <Route path="/marking/tests/:attemptId" element={<MarkTest />} />}
          {canMark && <Route path="/marking/assignments/:submissionId" element={<MarkAssignment />} />}
          {canMark && <Route path="/marking/sessions/:id" element={<SessionRegister />} />}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </>
  );
}

function TopBar({ isAdmin, canMark, canAuthor, canApprove, canGroups, canTrainingReport }: { isAdmin: boolean; canMark: boolean; canAuthor: boolean; canApprove: boolean; canGroups: boolean; canTrainingReport: boolean }) {
  const { user, signOut } = useSession();
  const waiting = useMarkingCount(canMark);
  const unread = useUnreadCount();
  const portal = usePortal();
  return (
    <header className="topbar">
      <Link to="/" className="topbar__brand" aria-label={`${portal.name} home`}>
        <Logo size="small" />
      </Link>

      {(isAdmin || canMark || canAuthor || canApprove || canGroups || canTrainingReport || ['read','update'].some(action => can(user?.permissions, 'administrator.events_engine.notifications.' + action))) && (
        <nav className="topbar__nav">
          <NavLink to="/" end>My learning</NavLink>
          {isAdmin && <NavLink to="/admin" end>Overview</NavLink>}
          {isAdmin && <NavLink to="/admin/people">People</NavLink>}
          {isAdmin && <NavLink to="/admin/courses">Courses</NavLink>}
          {canAuthor && !isAdmin && <NavLink to="/instructor/courses">Author courses</NavLink>}
          {canTrainingReport && <NavLink to="/admin/training-reports">Training summaries</NavLink>}
          {canApprove && <NavLink to="/admin/enrollment-requests">Enrollment requests</NavLink>}
          {(canGroups || canTrainingReport) && <NavLink to="/admin/programs">Programs</NavLink>}
          {can(user?.permissions, "administrator.reports") && <NavLink to="/admin/graduations">Graduation records</NavLink>}
          {canGroups && <NavLink to="/admin/cohorts">Cohorts</NavLink>}
          {isAdmin && <NavLink to="/admin/certificates">Certificates</NavLink>}
          {isAdmin && <NavLink to="/admin/files">Files</NavLink>}
          {canTrainingReport && <NavLink to="/admin/reports">Reports</NavLink>}
          {canTrainingReport && <NavLink to="/admin/saved-reports">Saved reports</NavLink>}
          {(isAdmin || canMark) && <NavLink to="/admin/question-stats">Questions</NavLink>}
          {['read','update'].some(action => can(user?.permissions, 'administrator.events_engine.notifications.' + action)) && <NavLink to="/admin/training-automations">Automations</NavLink>}
          {isAdmin && <NavLink to="/admin/notifications">Notifications</NavLink>}
          {isAdmin && <NavLink to="/admin/user-types">User types</NavLink>}
          {isAdmin && <NavLink to="/admin/passwords">Passwords</NavLink>}
          {isAdmin && <NavLink to="/admin/settings">Settings</NavLink>}
          {canMark && (
            <NavLink to="/marking">
              To mark{waiting > 0 && <span className="badge">{waiting}</span>}
            </NavLink>
          )}
        </nav>
      )}

      <div className="topbar__right">
        <NavLink to="/messages" className="topbar__link">
          Messages{unread > 0 && <span className="badge">{unread}</span>}
        </NavLink>
        <NavLink to="/catalog" className="topbar__link">Catalogue</NavLink>
        <NavLink to="/programs" className="topbar__link">My programs</NavLink>
        <NavLink to="/classes" className="topbar__link">My classes</NavLink>
        <NavLink to="/files" className="topbar__link">Files</NavLink>
        <NavLink to="/progress" className="topbar__link">My progress</NavLink>
        <NavLink to="/certificates" className="topbar__link">My certificates</NavLink>
        <NavLink to="/profile" className="topbar__who">{user?.firstName} {user?.lastName}</NavLink>
        <button type="button" className="btn btn--ghost" onClick={() => void signOut()}>Sign out</button>
      </div>
    </header>
  );
}

/**
 * How many things are waiting to be marked, for the badge in the nav. A failure
 * here is silent: a missing badge is better than a broken portal.
 */
function useMarkingCount(enabled: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const read = async () => {
      try {
        const q = await api.get<{ counts: { tests: number; assignments: number } }>('/instructor/marking');
        if (live) setCount(q.counts.tests + q.counts.assignments);
      } catch { /* leave the badge off */ }
    };
    void read();
    const timer = setInterval(read, 120_000);
    return () => { live = false; clearInterval(timer); };
  }, [enabled]);
  return count;
}

/**
 * Unread messages, for the badge on the envelope. Silent on failure for the
 * same reason as the marking count: the portal is more use than the number.
 */
function useUnreadCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let live = true;
    const read = async () => {
      try {
        const q = await api.get<{ unread: number }>('/messages/unread');
        if (live) setCount(q.unread);
      } catch { /* leave the badge off */ }
    };
    void read();
    const timer = setInterval(read, 60_000);
    return () => { live = false; clearInterval(timer); };
  }, []);
  return count;
}

/**
 * What the portal calls itself, and anything it is announcing.
 *
 * Public on purpose: the sign-in page carries the name too, and a portal that
 * says "Learnica" to somebody who was told to sign in to Qatar Care Training
 * looks like the wrong address.
 */
function usePortal() {
  const [portal, setPortal] = useState<{ name: string; announcement: string | null }>(
    { name: 'Learnica', announcement: null },
  );
  useEffect(() => {
    void (async () => {
      try {
        const r = await api.get<{ portal: { name: string; announcement: string | null } }>('/settings/public');
        setPortal(r.portal);
      } catch { /* the default name is a fine fallback */ }
    })();
  }, []);
  return portal;
}

function Announcement() {
  const { announcement } = usePortal();
  if (!announcement) return null;
  return <div className="announce" role="status">{announcement}</div>;
}

/**
 * Says, at the top of every page, that this is somebody else's portal.
 *
 * Above the top bar rather than below it, and it does not scroll away: the
 * way viewing as somebody goes wrong is forgetting that you are.
 */
function ViewingAsBanner() {
  const { user, refresh } = useSession();
  const portal = usePortal();
  const [busy, setBusy] = useState(false);
  if (!user?.impersonatedBy) return null;

  return (
    <div className="viewingas" role="status">
      <span>
        You are viewing {portal.name} as <strong>{user.firstName} {user.lastName}</strong>.
        Nothing here changes their password or how they are contacted.
      </span>
      <button className="btn btn--small" disabled={busy} onClick={async () => {
        setBusy(true);
        try { await api.post('/admin/people/stop-viewing-as'); await refresh(); }
        finally { setBusy(false); }
      }}>
        {busy ? 'Returning…' : 'Back to my account'}
      </button>
    </div>
  );
}
